import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import { Box, theme } from '@mrshmllw/smores-react'

import { Logo } from './Logo'
import { PostQuoteProgressBar } from '../../post-quote/components/PostQuoteProgressBar'
import TrustPilotImg from 'assets/images/trustpilot-stars-4.5.svg'

import { useBreakpoint, useEventTracking } from 'utils'

type HeaderProps = {
  progressBar?: ReactNode
  directFlowHeader?: boolean
  isEditMode?: boolean
}

export const Header: FC<HeaderProps> = ({ progressBar }) => {
  const track = useEventTracking()

  const deviceType = useBreakpoint(768)
  const isDesktop = deviceType === 'desktop'

  return (
    <HeaderWrapper>
      <ContentWrapper>
        <LogoBox>
          <a href="https://www.marshmallow.com" onClick={() => track('Clicked header logo')}>
            <Logo />
          </a>
          <TrustPilotBox>
            <TrustPilot src={TrustPilotImg} />
            <TrustPilotText>
              Rated <B>Excellent</B> on Trustpilot
            </TrustPilotText>
          </TrustPilotBox>
        </LogoBox>

        {progressBar && isDesktop && <Box>{progressBar}</Box>}
        <DesktopEmptyBox />
      </ContentWrapper>
      {progressBar && !isDesktop && (
        <Box mt="0" mb="8px">
          {progressBar}
        </Box>
      )}
    </HeaderWrapper>
  )
}

type PostQuoteHeaderProps = {
  hideProgressBar?: boolean
}

export const PostQuoteHeader: FC<PostQuoteHeaderProps> = ({ hideProgressBar }) => {
  return (
    <Header
      progressBar={
        !hideProgressBar ? (
          <ProgressBox flex width="100%" data-testid="progress-bar">
            <PostQuoteProgressBar />
          </ProgressBox>
        ) : undefined
      }
    />
  )
}

const LOGO_DESKTOP_WIDTH = 200

const HeaderWrapper = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 768px) {
    justify-content: center;
  }
`

const ContentWrapper = styled(Box)`
  width: 100%;
  max-width: 1280px;

  display: flex;
  justify-content: space-between;
  padding: 12px 16px 8px;
  align-items: center;
  gap: 12px;

  @media (min-width: 768px) {
    padding: 16px 24px;
    justify-content: space-between;
    gap: 16px;
  }
`

const LogoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: '6px';

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: ${LOGO_DESKTOP_WIDTH}px;
  }
`

const TrustPilot = styled.img`
  width: 56px;

  @media (min-width: 768px) {
    width: 64px;
  }
`

const TrustPilotBox = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 4px;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }

  @media (max-width: 350px) {
    column-gap: 4px;
  }
`

const TrustPilotText = styled.p`
  font-size: 10px;
  white-space: nowrap;
`

const B = styled.b`
  font-weight: ${theme.font.weight.medium};
`

/**
 * Used to center the progress bar, since the logo is on the left
 * We balance it out with an empty box on the right
 */
const DesktopEmptyBox = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    width: ${LOGO_DESKTOP_WIDTH}px;
  }
`

const ProgressBox = styled(Box)`
  justify-content: center;
`
